/**
 * Application Routes Constants
 *
 * Centralized location for all route definitions to make updates easier.
 * All routes should be defined here and imported throughout the application.
 */

/**
 * Generic route builder for routes that use handles
 * @param baseRoute - Base route from ROUTES constant
 * @param handle - Handle to append to the route
 * @returns Complete route string
 */
export const buildHandleRoute = (baseRoute: string, handle?: string | null) => `/${baseRoute}/${handle || ""}`

// Base routes
export const ROUTES = {
  // Home
  HOME: "",

  // Spanish routes (current)
  CART: "carrito",
  CATEGORIES: "categorias",
  COLLECTIONS: "colecciones",
  PRODUCTS: "productos",
  SEARCH: "busqueda",
  STORE: "store",

  // Account routes
  ACCOUNT: "account",
  ACCOUNT_ADDRESSES: "account/addresses",
  ACCOUNT_ORDERS: "account/orders",
  ACCOUNT_PROFILE: "account/profile",

  // Checkout routes
  CHECKOUT: "checkout",
  ORDER_CONFIRMED: "order/confirmed",
  PARTIAL_PAYMENT: "partial-payment",

  // Other routes
  HOW_TO_BUY: "como-comprar",
  VERIFICATION: "verificacion",
} as const

export const LEGACY_ROUTES = {
  CART: "cart",
  CATEGORIES: "categories",
  COLLECTIONS: "collections",
  PRODUCTS: "products",
} as const

// Route builders for dynamic routes
export const buildRoute = {
  /**
   * Build product route with handle
   * @param handle - Product handle
   * @returns Product route string
   */
  product: (handle?: string | null) => buildHandleRoute(ROUTES.PRODUCTS, handle),

  /**
   * Build category route with category path
   * @param categoryPath - Array of category segments or single category
   * @returns Category route string
   */
  category: (categoryPath?: string | (string | null | undefined)[]) => {
    if (!categoryPath) return `/${ROUTES.CATEGORIES}`
    const path = Array.isArray(categoryPath) ? categoryPath.filter((val) => val !== null && val !== undefined).join("/") : categoryPath
    return buildHandleRoute(ROUTES.CATEGORIES, path)
  },

  /**
   * Build collection route with handle
   * @param handle - Collection handle
   * @returns Collection route string
   */
  collection: (handle?: string | null) => buildHandleRoute(ROUTES.COLLECTIONS, handle),

  /**
   * Build search route with query
   * @param query - Search query
   * @returns Search route string
   */
  search: (query?: string) => {
    if (!query) return `/${ROUTES.SEARCH}`
    return `/${ROUTES.SEARCH}?search=${encodeURIComponent(query)}`
  },

  /**
   * Build order confirmation route with order ID
   * @param orderId - Order ID
   * @returns Order confirmation route string
   */
  orderConfirmed: (orderId?: string) => `/${ROUTES.ORDER_CONFIRMED}/${orderId || ""}`,

  /**
   * Build account order details route with order ID
   * @param orderId - Order ID
   * @returns Account order details route string
   */
  accountOrderDetails: (orderId?: string) => `/${ROUTES.ACCOUNT_ORDERS}/details/${orderId || ""}`,
}

// Route validation helpers
export const isRoute = {
  /**
   * Check if pathname is a category page
   * @param pathname - Current pathname
   * @returns True if on category page
   */
  category: (pathname: string) => pathname.includes(ROUTES.CATEGORIES),

  /**
   * Check if pathname is a collection page
   * @param pathname - Current pathname
   * @returns True if on collection page
   */
  collection: (pathname: string) => pathname.includes(ROUTES.COLLECTIONS),

  /**
   * Check if pathname is a product page
   * @param pathname - Current pathname
   * @returns True if on product page
   */
  product: (pathname: string) => pathname.includes(ROUTES.PRODUCTS),

  /**
   * Check if pathname is a category or collection page
   * @param pathname - Current pathname
   * @returns True if on category or collection page
   */
  categoryOrCollection: (pathname: string) => isRoute.category(pathname) || isRoute.collection(pathname),

  /**
   * Check if pathname is a cart page
   * @param pathname - Current pathname
   * @returns True if on cart page
   */
  cart: (pathname: string) => pathname.includes(ROUTES.CART),

  /**
   * Check if pathname is a search page
   * @param pathname - Current pathname
   * @returns True if on search page
   */
  search: (pathname: string) => pathname.includes(ROUTES.SEARCH),
}

// Type exports for better type safety
export type RouteKey = keyof typeof ROUTES
export type LegacyRouteKey = keyof typeof LEGACY_ROUTES
